import React from "react";
import { useExternalQuoteRequestsControllerFindContractDocuments } from "@semo/api-contracts";
import { cn } from "@semo/shared-ui";
import { FileText, Download, CheckCircle2, Clock } from "@semo/icons";

interface ContractDocumentsSectionProps {
  /** 견적요청 식별자 — UUID · 공고번호 · bid id 모두 받는다(서버가 해석한다). */
  requestId: string;
}

/**
 * 견적 상세의 [계약서류] — **낙찰된 뒤에만 나타난다.**
 *
 * 종전에는 이 서류들이 채팅방 안에서만 오갔다. 그래서 「무엇이 남았는지」를 보려면 계약마다
 * 채팅을 열어 스크롤해야 했고, 발주기관은 여러 공급사 몫을 한눈에 볼 방법이 없었다.
 * 입찰공고가 낙찰 후 서류를 공고 화면에 바로 붙여 주듯, 여기서도 같은 자리에 붙인다.
 *
 * 계약이 없으면 **아무것도 그리지 않는다**(빈 카드도 안 그린다) — 낙찰 전 화면에 「서류 0건」
 * 이 떠 있으면 «내야 할 것이 없다» 로 읽힌다.
 *
 * 제출은 여전히 채팅이 한다. 여기서 또 올리게 만들면 입력형 서류(값을 채워야 생성되는 것)와
 * 파일형 서류의 두 흐름을 한 번 더 구현하게 되고, 두 곳의 상태가 갈린다.
 */
export const ContractDocumentsSection: React.FC<
  ContractDocumentsSectionProps
> = ({ requestId }) => {
  const { data: response, isLoading } =
    useExternalQuoteRequestsControllerFindContractDocuments(requestId, {
      query: { enabled: !!requestId },
    });

  const groups = response?.data ?? [];

  // 로딩 중이거나 계약이 아직 없으면 자리를 차지하지 않는다.
  if (isLoading || groups.length === 0) return null;

  return (
    <section className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <header className="flex items-center justify-between mb-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900">
          <FileText className="w-4 h-4 text-slate-500" />
          계약서류
        </h2>
        <span className="text-xs text-slate-500">
          제출은 견적 채팅에서 진행됩니다
        </span>
      </header>

      <div className="space-y-4">
        {groups.map((group) => (
          <div
            key={group.contractId}
            className="rounded-lg border border-slate-200"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-slate-900">
                  {group.contractNumber}
                </span>
                {group.supplierName && (
                  <span className="text-slate-500">{group.supplierName}</span>
                )}
              </div>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium",
                  group.pendingCount > 0
                    ? "bg-amber-100 text-amber-700"
                    : "bg-emerald-100 text-emerald-700",
                )}
              >
                {group.pendingCount > 0
                  ? `미제출 ${group.pendingCount}건`
                  : "제출 완료"}
              </span>
            </div>

            {group.documents.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-500">
                요청된 서류가 없습니다.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {group.documents.map((doc) => {
                  const completed = doc.status === "completed";
                  return (
                    <li
                      key={`${group.contractId}-${doc.paperCode}`}
                      className="flex items-center justify-between gap-3 px-4 py-3"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        {completed ? (
                          <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                        ) : (
                          <Clock className="w-4 h-4 shrink-0 text-amber-500" />
                        )}
                        <span className="truncate text-sm text-slate-800">
                          {doc.name}
                        </span>
                      </div>

                      {doc.fileUrl ? (
                        <a
                          href={doc.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex shrink-0 items-center gap-1 text-sm text-primary-text hover:text-primary-hover"
                        >
                          <Download className="w-3.5 h-3.5" />
                          {/* 파일명은 길어서 줄바꿈을 만든다 — 동작이 이름이다. */}
                          받기
                        </a>
                      ) : (
                        <span className="shrink-0 text-xs text-slate-400">
                          미제출
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
